import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchBar = ({ onSearch, className = '' }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const searchRef = useRef(null);

  const searchableContent = [
    { title: 'Getting Started', section: 'API Basics', anchor: '#getting-started' },
    { title: 'Authentication Overview', section: 'Authentication', anchor: '#auth-overview' },
    { title: 'API Keys', section: 'Authentication', anchor: '#api-keys' },
    { title: 'OAuth 2.0', section: 'Authentication', anchor: '#oauth' },
    { title: 'Products API', section: 'Endpoints', anchor: '#products-api' },
    { title: 'Categories API', section: 'Endpoints', anchor: '#categories-api' },
    { title: 'Orders API', section: 'Endpoints', anchor: '#orders-api' },
    { title: 'Webhooks API', section: 'Endpoints', anchor: '#webhooks-api' },
    { title: 'Error Handling', section: 'Best Practices', anchor: '#error-handling' },
    { title: 'Rate Limiting', section: 'API Basics', anchor: '#rate-limiting' },
    { title: 'HTTP Status Codes', section: 'API Basics', anchor: '#status-codes' },
    { title: 'Terms & Definitions', section: 'Glossary', anchor: '#terms-definitions' }
  ];

  useEffect(() => {
    if (query?.length > 2) {
      const filtered = searchableContent?.filter(item =>
        item?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.section?.toLowerCase()?.includes(query?.toLowerCase())
      );
      setResults(filtered?.slice(0, 8));
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (anchor) => {
    const element = document.querySelector(anchor);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setQuery('');
    setIsOpen(false);
    if (onSearch) {
      onSearch(anchor);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <Input
          type="search"
          placeholder="Search documentation..."
          value={query}
          onChange={(e) => setQuery(e?.target?.value)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-4"
        />
        <Icon 
          name="Search" 
          size={18} 
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
        />
      </div>
      {isOpen && results?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-modal z-50 max-h-80 overflow-y-auto">
          <div className="py-2">
            {results?.map((result, index) => (
              <button
                key={index}
                onClick={() => handleResultClick(result?.anchor)}
                className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-muted transition-colors"
              >
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {result?.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {result?.section}
                  </div>
                </div>
                <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
      {isOpen && query?.length > 2 && results?.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-modal z-50">
          <div className="px-4 py-6 text-center">
            <Icon name="Search" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No results found for "{query}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;